
import { useState, useCallback, useEffect } from "react";
// Swiper core & modules
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination, A11y } from 'swiper/modules';
// Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
// Local styles
import './scss/_gallerySlider.css';

type ImageItem = {
    img: string;
    alt?: string;
    width?: number;
    height?: number;
};

interface GallerySliderProps {
    images: ImageItem[];
    autoDelayMs?: number; // за замовчуванням 10000 (10 сек)
}

export default function GallerySlider({
                                          images,
                                          autoDelayMs = 10000,
                                      }: GallerySliderProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number>(0);

    const openModal = useCallback((index: number) => {
        setActiveIndex(index);
        setIsOpen(true);
        // Заблокуємо скрол сторінки під модалкою
        document.documentElement.style.overflow = 'hidden';
    }, []);

    const closeModal = useCallback(() => {
        setIsOpen(false);
        document.documentElement.style.overflow = '';
    }, []);

    // Закриття по Esc
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeModal();
            if (e.key === 'ArrowRight') setActiveIndex((i) => Math.min(i + 1, images.length - 1));
            if (e.key === 'ArrowLeft') setActiveIndex((i) => Math.max(i - 1, 0));
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, closeModal, images.length]);

    const prev = () => setActiveIndex((i) => Math.max(i - 1, 0));
    const next = () => setActiveIndex((i) => Math.min(i + 1, images.length - 1));

    return (
        <div className="gallery-wrapper">
            {/* Слайдер */}
            <Swiper
                modules={[Autoplay, Navigation, Pagination, A11y]}
                spaceBetween={16}
                slidesPerView={1}
                loop={images.length > 1} // щоб не ламався при 1 слайді
                navigation
                pagination={{ clickable: true }}
                breakpoints={{
                    600: { slidesPerView: 2 },
                    900: { slidesPerView: 3 },
                    1200: { slidesPerView: 4 },
                }}
                aria-label="Bildekarusell"
            >
                {images.map((img, idx) => (
                    <SwiperSlide key={idx}>
                        <button
                            className="slide-btn"
                            onClick={() => openModal(idx)}
                            aria-label={`Åpne bilde ${idx + 1} i fullskjerm`}
                        >
                            <img
                                src={img.img}
                                alt={img.alt ?? `Bilde ${idx + 1}`}
                                loading="lazy"
                                decoding="async"
                                width={img.width ?? 1200}
                                height={img.height ?? 600}
                            />
                        </button>
                    </SwiperSlide>
                ))}
            </Swiper>

            {/* Модальне вікно */}
            {isOpen && (
                <div
                    className="modal-backdrop"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Forstørret bilde"
                    onClick={closeModal}
                >
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div
                            className="modal-close"
                            onClick={closeModal}
                            aria-label="Lukk"
                        >
                            ×
                        </div>

                        <div className="modal-body">
                            <div
                                className={`nav-arrow left `}
                                onClick={prev}
                                aria-label="Forrige bilde"
                            >
                                ‹
                            </div>

                            <img
                                className="modal-image"
                                src={images[activeIndex].img}
                                alt={images[activeIndex].alt ?? `Bilde ${activeIndex + 1}`}
                            />

                            <div
                                className={`nav-arrow right `}
                                onClick={next}
                                aria-label="Neste bilde"
                            > ›
                            </div>
                        </div>

                        <div className="modal-counter">
                            {activeIndex + 1} / {images.length}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
